import { useState } from 'react';
import {
  App,
  Badge,
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useDeactivateEnterprise,
  useEnterprises,
  useReactivateEnterprise,
} from '@/hooks/useEnterprises';
import { useAuthStore } from '@/stores/authStore';
import type { EnterpriseDto } from '@/types/enterprise';
import { EnterpriseFormModal } from './EnterpriseFormModal';

const { Title, Text } = Typography;

export function EnterprisesPage() {
  const { message } = App.useApp();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingEnterprise, setEditingEnterprise] = useState<EnterpriseDto | null>(null);

  const { data, isLoading, refetch, isFetching } = useEnterprises({
    page,
    pageSize,
    search: search || undefined,
    includeInactive,
  });

  const deactivate = useDeactivateEnterprise();
  const reactivate = useReactivateEnterprise();

  function handleNew() {
    setEditingEnterprise(null);
    setModalOpen(true);
  }

  function handleEdit(enterprise: EnterpriseDto) {
    setEditingEnterprise(enterprise);
    setModalOpen(true);
  }

  async function handleDeactivate(id: number) {
    try {
      await deactivate.mutateAsync(id);
      message.success('Enterprise desactivada.');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al desactivar.');
    }
  }

  async function handleReactivate(id: number) {
    try {
      await reactivate.mutateAsync(id);
      message.success('Enterprise reactivada.');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al reactivar.');
    }
  }

  const columns: TableProps<EnterpriseDto>['columns'] = [
    {
      title: 'Nombre',
      dataIndex: 'displayNameEs',
      key: 'displayNameEs',
      sorter: true,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.displayNameEs}</Text>
          {record.displayName !== record.displayNameEs && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.displayName}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Google Enterprise ID',
      dataIndex: 'googleEnterpriseId',
      key: 'googleEnterpriseId',
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: 'Contacto',
      key: 'contact',
      render: (_, record) =>
        record.contactEmail || record.contactName ? (
          <Space direction="vertical" size={0}>
            {record.contactName && <Text>{record.contactName}</Text>}
            {record.contactEmail && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.contactEmail}
              </Text>
            )}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Stats',
      key: 'stats',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Dispositivos">
            <Badge count={record.devicesCount} showZero color="var(--color-primary)" overflowCount={9999} />
          </Tooltip>
          <Tooltip title="Perfiles">
            <Badge count={record.profilesCount} showZero color="var(--color-success)" overflowCount={9999} />
          </Tooltip>
          <Tooltip title="Usuarios">
            <Badge count={record.usersCount} showZero color="var(--color-warning)" overflowCount={9999} />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) =>
        isActive ? <Tag color="success">Activa</Tag> : <Tag color="default">Inactiva</Tag>,
    },
    {
      title: 'Creada',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      align: 'right',
      render: (_, record) =>
        isSuperAdmin ? (
          <Space size="small">
            <Tooltip title="Editar">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            {record.isActive ? (
              <Popconfirm
                title="Desactivar Enterprise"
                description={`Confirmar desactivacion de "${record.displayNameEs}"?`}
                onConfirm={() => handleDeactivate(record.id)}
                okText="Desactivar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Desactivar">
                  <Button type="text" size="small" danger icon={<StopOutlined />} />
                </Tooltip>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Reactivar Enterprise"
                description={`Confirmar reactivacion de "${record.displayNameEs}"?`}
                onConfirm={() => handleReactivate(record.id)}
                okText="Reactivar"
                cancelText="Cancelar"
              >
                <Tooltip title="Reactivar">
                  <Button type="text" size="small" icon={<ReloadOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Solo lectura
          </Text>
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          Enterprises
        </Title>
        {isSuperAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleNew}>
            Nueva Enterprise
          </Button>
        )}
      </div>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
          <Space>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Buscar por nombre, ID, email..."
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 320 }}
              onPressEnter={() => setPage(1)}
            />
            <Button onClick={() => setPage(1)}>Buscar</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refrescar
            </Button>
          </Space>
          <Space>
            <Text type="secondary">Incluir inactivas:</Text>
            <Switch checked={includeInactive} onChange={setIncludeInactive} />
          </Space>
        </Space>

        <Table<EnterpriseDto>
          columns={columns}
          dataSource={data?.items}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.totalCount ?? 0,
            onChange: (newPage, newSize) => {
              setPage(newPage);
              if (newSize !== pageSize) setPageSize(newSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} en total`,
          }}
        />
      </Card>

      <EnterpriseFormModal
        open={modalOpen}
        enterprise={editingEnterprise}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
